/**
 * The stress diagram as a standalone HTML document for the server PDF.
 *
 * Only the CSS rules that match the diagram are sent. Copying every stylesheet on the
 * page made the payload ~780KB, which the PDF renderer spent ~2s parsing, and pulled
 * in web fonts it had to fetch.
 */

// Pseudo-elements can't be tested with matches(); test the element they attach to.
const stripPseudo = (selector: string) =>
  selector.replace(/::?(before|after|placeholder|marker|selection|first-line|first-letter)\b/gi, '');

const ruleMatches = (root: Element, selectorText: string): boolean =>
  selectorText.split(',').some(part => {
    const sel = stripPseudo(part.trim()) || '*';
    try {
      return root.matches(sel) || root.querySelector(sel) !== null;
    } catch {
      return false;
    }
  });

function collectRules(root: Element, rules: CSSRuleList, out: string[]) {
  for (const rule of Array.from(rules)) {
    if (rule instanceof CSSStyleRule) {
      if (ruleMatches(root, rule.selectorText)) out.push(rule.cssText);
    } else if (rule instanceof CSSMediaRule) {
      const inner: string[] = [];
      collectRules(root, rule.cssRules, inner);
      if (inner.length) out.push(`@media ${rule.conditionText} {\n${inner.join('\n')}\n}`);
    }
    // @font-face, @import and @keyframes are dropped: the PDF uses its own font.
  }
}

export function captureDiagramHtml(element: Element | null): string {
  if (!element) return '';
  const css: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      collectRules(element, sheet.cssRules, css);
    } catch {
      // cross-origin sheet; its rules are unreadable
    }
  }
  return `<html><head><style>${css.join('\n')}\nbody{background:white!important;}.box{background:white!important;}</style></head><body>${element.outerHTML}</body></html>`;
}
